import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { ShippingLocation, Variation } from '@/types';
import { useStoreInfo } from '@/lib/store';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getDomain() {
  return window.location.hostname;
}

export function getStoreId() {
  const domain = getDomain();
  const storeId = domain.split('.')[0];
  return storeId;
}

/**
 * Converts currency symbol or code to ISO currency code for Intl.NumberFormat
 * Returns null if currency is empty or invalid
 */
export function getCurrencyCode(currency: string | null | undefined): string | null {
  if (!currency || currency.trim() === '') {
    return null;
  }

  const trimmed = currency.trim();

  // Map common currency symbols to ISO codes
  const symbolToCode: Record<string, string> = {
    '₦': 'NGN',
    '$': 'USD',
    '€': 'EUR',
    '£': 'GBP',
    '¥': 'JPY',
    '₹': 'INR',
    'N': 'NGN', // Sometimes "N" is used for Naira
    'NGN': 'NGN',
    'USD': 'USD',
    'EUR': 'EUR',
    'GBP': 'GBP',
    'JPY': 'JPY',
    'INR': 'INR',
  };

  // Check if it's a known symbol or code
  if (symbolToCode[trimmed]) {
    return symbolToCode[trimmed];
  }

  // If it's already a valid ISO code (3 uppercase letters), use it
  if (/^[A-Z]{3}$/.test(trimmed.toUpperCase())) {
    return trimmed.toUpperCase();
  }

  // If it's a single character that might be a symbol, return null
  // Otherwise, try to use it as-is (might be invalid, but let Intl handle it)
  return trimmed.length <= 3 ? trimmed.toUpperCase() : null;
}

/**
 * Formats price using currency value directly from store info
 * Returns formatted price with currency value from currenCy field, or plain number if no currency is set
 */
export function formatPrice(price: number | string): string {
  const { storeInfo } = useStoreInfo.getState();
  const currencyValue = storeInfo?.currenCy;

  const numericPrice = typeof price === 'string' ? parseInt(price, 10) : price;
  
  // Format the number with commas
  const formattedNumber = numericPrice.toLocaleString('en-US');

  // If no currency value, return plain formatted number
  if (!currencyValue || currencyValue.trim() === '') {
    return formattedNumber;
  }

  // Use the currency value directly as-is (could be symbol like "₦" or code like "NGN")
  return `${currencyValue.trim()}${formattedNumber}`;
}

export function formatPriceRange(variations: Variation[]) {
  if (!variations || variations.length === 0) return '';

  const prices = variations.map(v => parseInt(v.variation_amount));
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);

  if (minPrice === maxPrice) {
    return formatPrice(minPrice);
  }

  return `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;
}

export function getLowestPrice(variations: Variation[]) {
  if (!variations || variations.length === 0) return 0;
  return Math.min(...variations.map(v => parseInt(v.variation_amount)));
}

export function truncateText(text: string, length: number) {
  if (text.length <= length) return text;
  return text.slice(0, length) + '...';
}

export function fetchWithTimeout(resource: string, options: RequestInit & { timeout?: number } = {}) {
  const { timeout = 10000 } = options;
  
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  
  try {
    const response = fetch(resource, {
      ...options,
      headers: {
        'Accept': 'application/json',
        ...(isIOS ? {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          'If-None-Match': new Date().getTime().toString()
        } : {}),
        ...(options.headers || {})
      },
      ...(isIOS ? {
        signal: controller.signal,
        cache: 'no-store',
        credentials: 'omit'
      } : {
        signal: controller.signal
      })
    });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please check your connection and try again.');
    }
    throw error;
  }
}

export function setCache(key: string, data: any, days = 10) {
  const item = {
    value: data,
    timestamp: new Date().getTime() + (days * 24 * 60 * 60 * 1000)
  };
  try {
    localStorage.setItem(key, JSON.stringify(item));
  } catch (error) {
    console.warn('Failed to set cache:', error);
  }
}

export function getCache(key: string) {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;

    const { value, timestamp } = JSON.parse(item);
    if (new Date().getTime() > timestamp) {
      localStorage.removeItem(key);
      return null;
    }
    return value;
  } catch (error) {
    console.warn('Failed to get cache:', error);
    return null;
  }
}

export function setShippingPreferences(country: string, location: string) {
  setCache('shipping_country', country, 30);
  setCache('shipping_location', location, 30);
}

export function getShippingPreferences(): { country: string | null; location: string | null } {
  return {
    country: getCache('shipping_country'),
    location: getCache('shipping_location')
  };
}

export function filterLocations(locations: ShippingLocation[], search: string): ShippingLocation[] {
  const searchLower = search.toLowerCase();
  return locations.filter(
    location => 
      location.name?.toLowerCase().includes(searchLower) ||
      location.country.toLowerCase().includes(searchLower)
  );
}

export function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}

export function validateEmail(email: string): boolean {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

export function validatePhone(phone: string): boolean {
  return phone.length >= 10 && /^\+?[\d\s-]+$/.test(phone);
}

export function getCountries(locations: ShippingLocation[]): string[] {
  return [...new Set(locations.map(location => location.country))];
}

export function getLocationsByCountry(locations: ShippingLocation[], country: string): ShippingLocation[] {
  return locations.filter(location => location.country === country);
}

export async function retryWithBackoff(fn: () => Promise<any>, maxRetries = 3, baseDelay = 1000): Promise<any> {
  let lastError;
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (i === maxRetries - 1) break;
      await new Promise(resolve => setTimeout(resolve, baseDelay * Math.pow(2, i)));
    }
  }
  throw lastError;
}

/**
 * Resolve image path returned from API to a usable full URL.
 * If `imagePath` is already a full URL it will be returned as-is.
 * Otherwise it will prefix with `VITE_IMAGE_BASE_URL` and ensure `product_images` is present.
 */
export function getFullImageUrl(imagePath?: string | null): string {
  if (!imagePath) return '';
  if (/^https?:\/\//i.test(imagePath)) return imagePath;
  const base = (import.meta.env.VITE_IMAGE_BASE_URL || '').replace(/\/$/, '');
  const cleaned = imagePath.replace(/^\/+/, '');
  if (cleaned.startsWith('product_images')) return `${base}/${cleaned}`;
  return `${base}/product_images/${cleaned}`;
}

/**
 * Return an optimized image URL (served by backend resize endpoint) when enabled.
 * Falls back to getFullImageUrl when not enabled.
 */
export function getOptimizedImageUrl(imagePath?: string | null, width = 400): string {
  if (!imagePath) return '';
  const enabled = import.meta.env.VITE_ENABLE_IMAGE_RESIZER === 'true';
  const full = getFullImageUrl(imagePath);
  if (!enabled) return full;
  try {
    const encoded = encodeURIComponent(imagePath.replace(/^\/+/, ''));
    const apiBase = import.meta.env.VITE_API_ENDPOINT || '';
    // URL: <API>/images/resize?path=<path>&w=<width>
    return `${apiBase.replace(/\/$/, '')}/images/resize?path=${encoded}&w=${width}`;
  } catch (err) {
    return full;
  }
}